/** Who can do what - the one table a screen reads to say it (Q-05, G-P6-02, -03, -08, G-P3-07,
 * G-Wviewer-09 and the family).
 *
 * **Display only: the server stays the authority.** Every row names the route that enforces it
 * (`route`, spelled exactly as `backend/tests/unit/test_route_role_matrix.py` spells it), and
 * `whoCan.test.ts` parses that file and fails if a row's roles differ from the roles the server
 * admits. So this is a *mirror with a witness*, not a second opinion: the day a route's policy
 * changes, the test says which row is wrong.
 *
 * Why one table. Screens each wrote their own *"who can"* sentence, from a private list of roles
 * (`MAY_READ` in `Ledger.tsx`, `MAY_WRITE` in `RecordWork.tsx`, `MAY_DELIVER` in `Transition.tsx`),
 * and four wrote none. A sentence typed beside a list of roles is two homes for one fact; this is
 * the one home for the sentence and the roles together. `NotYourRole` is the one component that says
 * it.
 *
 * `routeTable.ts` owns *which places a role is offered*; this owns *which actions inside a place a
 * role may take, and the words for who may*. The route table's `reads` (`READS`, below) are the
 * places' own reads, so a place is never offered to a role whose first read the server refuses
 * (G-P6-08: a Contributor was offered the Audit ledger and Activity, and `GET /ledger` is 403).
 */

import { ROLES, type Role } from "./routeTable";

/** One thing a role may or may not do. */
export interface Capability {
  /** The server route that enforces it, as the matrix spells it (`METHOD /api/v1/path`). */
  route: string;
  /** What the people who can do it do, in the third person singular, for the end of a sentence that
   * starts with the roles: *"An Admin or an Approver ... adds a vendor."* */
  does: string;
  /** The roles the server admits. Written out, never computed: a role added to the server and not
   * here is *not offered the action*, which is the safe default for a control. */
  roles: readonly Role[];
}

/** Every action a screen words as *who can*. Add a row, name its route, and the test checks it. */
export const WHO_CAN = {
  uploadFile: {
    route: "POST /api/v1/evidence/uploads",
    does: "uploads a bill or a statement",
    roles: ["admin", "approver", "contributor"],
  },
  openImport: {
    route: "GET /api/v1/evidence/{document_id}/import/preview",
    does: "opens an import",
    roles: ["admin", "approver", "contributor"],
  },
  verifyDocument: {
    route: "POST /api/v1/evidence/{document_id}/verify",
    does: "reviews and accepts a bill",
    roles: ["admin", "approver"],
  },
  rejectFile: {
    route: "POST /api/v1/evidence/{document_id}/reject",
    does: "rejects a file",
    roles: ["admin", "approver"],
  },
  addVendor: {
    route: "POST /api/v1/sources/vendors",
    does: "adds or changes a vendor",
    roles: ["admin", "approver"],
  },
  addAccount: {
    route: "POST /api/v1/sources/accounts",
    does: "adds a supply account",
    roles: ["admin", "approver"],
  },
  requestSupplierData: {
    route: "POST /api/v1/supplier-exchange/requests",
    does: "asks a supplier for their data",
    roles: ["admin", "approver", "contributor"],
  },
  putRiskOnRegister: {
    route: "POST /api/v1/risks",
    does: "puts a risk on the register",
    roles: ["admin", "approver"],
  },
  defineRiskScale: {
    route: "POST /api/v1/risks/scales",
    does: "defines the scale a risk is scored on",
    roles: ["admin"],
  },
  rescoreRisk: {
    route: "POST /api/v1/risks/{risk_id}/rescore",
    does: "assesses a risk again",
    roles: ["admin", "approver"],
  },
  reopenRisk: {
    route: "POST /api/v1/risks/{risk_id}/reopen",
    does: "reopens a closed risk",
    roles: ["admin", "approver"],
  },
  rewordRisk: {
    route: "POST /api/v1/risks/{risk_id}/wording",
    does: "rewrites what a risk is complete when, or what would bring its review forward",
    roles: ["admin", "approver"],
  },
  addInitiative: {
    route: "POST /api/v1/plan/initiatives",
    does: "adds an initiative to the plan",
    roles: ["admin", "approver", "contributor"],
  },
  setTarget: {
    route: "POST /api/v1/plan/targets",
    does: "sets a target",
    roles: ["admin", "approver"],
  },
  readRecommendations: {
    route: "GET /api/v1/plan/recommendations",
    does: "reads the recommendations",
    roles: ["admin", "approver", "contributor", "viewer"],
  },
  readLedger: {
    route: "GET /api/v1/ledger",
    does: "reads the audit ledger",
    roles: ["admin", "approver", "auditor"],
  },
  readActivity: {
    route: "GET /api/v1/activity",
    does: "reads the activity log",
    roles: ["admin", "approver", "auditor"],
  },
  decideApproval: {
    route: "POST /api/v1/approval-requests/{request_id}/decisions",
    does: "decides an approval",
    roles: ["admin", "approver"],
  },
  readDecided: {
    route: "GET /api/v1/approval-requests/decided",
    does: "sees what has been decided",
    roles: ["admin", "approver"],
  },
  submitForApproval: {
    route: "POST /api/v1/records/{record_id}/approval-requests",
    does: "puts a record into review",
    roles: ["admin", "approver", "contributor"],
  },
  takeWork: {
    route: "POST /api/v1/work/items/take",
    does: "takes a bill to work on",
    roles: ["admin", "approver", "contributor"],
  },
  correctLease: {
    route: "POST /api/v1/facilities/{facility_id}/lease/corrections",
    does: "corrects a site's lease answer, or records that it changed on a date",
    roles: ["admin"],
  },
  chooseFilingBoundary: {
    route: "PUT /api/v1/reports/obligations/{obligation_id}/consolidation",
    does: "chooses what a filing covers of its group",
    roles: ["admin", "approver"],
  },
  administer: {
    route: "GET /api/v1/administration",
    does: "runs the workspace's administration",
    roles: ["admin"],
  },
} as const satisfies Record<string, Capability>;

export type Action = keyof typeof WHO_CAN;

/** The roles that may take an action. */
export function whoCan(action: Action): readonly Role[] {
  return WHO_CAN[action].roles;
}

/** Whether a role may take an action. An unknown or absent role may not. */
export function mayDo(role: string | undefined, action: Action): boolean {
  return role !== undefined && (WHO_CAN[action].roles as readonly string[]).includes(role);
}

const NAME: Readonly<Record<Role, string>> = {
  admin: "Admin",
  approver: "Approver",
  contributor: "Contributor",
  viewer: "Viewer",
  auditor: "Auditor",
};

/** A role as a person is called by it, or `undefined` for a word this table has not heard of. */
export function roleName(role: string | undefined): string | undefined {
  return (ROLES as readonly string[]).includes(role ?? "") ? NAME[role as Role] : undefined;
}

function article(name: string): string {
  return /^[AEIOU]/.test(name) ? "an" : "a";
}

/** *"an Admin, an Approver or a Contributor"* - roles in the product's own order, not the order
 * they were listed in, so two sentences naming the same roles name them the same way. A word that is
 * not a role is dropped rather than printed: the server's `needs` is a list of role values, but a
 * sixth role it adds is not one this screen has a name for. */
export function rolesPhrase(roles: readonly string[]): string {
  const names = ROLES.filter((role) => roles.includes(role)).map((role) => `${article(NAME[role])} ${NAME[role]}`);
  if (names.length <= 1) return names[0] ?? "a different role";
  return `${names.slice(0, -1).join(", ")} or ${names[names.length - 1]}`;
}

function capital(text: string): string {
  return text.charAt(0).toUpperCase() + text.slice(1);
}

/** The sentence that says who can: *"An Admin or an Approver adds or changes a vendor."* */
export function whoCanSentence(action: Action): string {
  const cap = WHO_CAN[action];
  return `${capital(rolesPhrase(cap.roles))} ${cap.does}.`;
}

/** The sentence for a refused read. The reader's own role is named when it is known, because "needs a
 * different role" asked them to guess which they were. */
export function refusedSentence(needs: readonly string[], role: string | undefined): string {
  const you = roleName(role);
  const named = needs.length > 0 ? `Only ${rolesPhrase(needs)} can use this` : "Your role cannot use this";
  return you ? `${named}; you are ${article(you)} ${you}.` : `${named}.`;
}

/** The roles a place's tab needs to read, beyond the ones every role may read. A tab with no entry
 * reads only what every role reads (`read` in the matrix) - and `whoCan.test.ts` holds the entries
 * to the route table: every role a tab is offered to must be admitted to every action listed.
 *
 * Keyed by `tabKey` (`routeTable.ts`). */
export const READS: Readonly<Record<string, readonly Action[]>> = {
  "/ledger": ["readLedger"],
  "/activity": ["readActivity"],
  "/transition?tab=recommendations": ["readRecommendations"],
  "/approvals?tab=decided": ["readDecided"],
};

/** Every action, for a test that wants to walk them. */
export const ACTIONS = Object.keys(WHO_CAN) as readonly Action[];

/** The roles that administer the workspace - the Admin place and every screen that was a tab of Settings. */
export const ADMIN_ONLY: readonly string[] = WHO_CAN.administer.roles;
